import { Editor, PropertyProps } from "grapesjs";

export const CoreAdjustmentsPlugin = () => (editor: Editor) => {
  console.log("🛠️ CoreAdjustmentsPlugin init");
  addBuiltinProperties(editor);
  configureBuiltInTypes(editor);

  editor.once("load", () => {
    configureSections(editor);
    configureTypography(editor);
  });
};

const addBuiltinProperties = (editor: Editor) => {
  const { StyleManager } = editor;

  const gapProperty = {
    type: "number",
    label: "Gap",
    property: "gap",
    unit: "px",
    units: ["px"],
    default: "0",
    min: 0,
    requires: {
      display: ["flex"],
    },
  } as PropertyProps;

  const objectFitProperty = {
    type: "select",
    toRequire: true,
    label: "Image fit",
    property: "object-fit",
    default: "fill",
    options: [
      { id: "cover", label: "Cover" },
      { id: "contain", label: "Contain" },
      { id: "fill", label: "Fill" },
      { id: "none", label: "None" },
      { id: "scale-down", label: "Scale down" },
    ],
  } as PropertyProps;

  // Add gap property to Flex sector
  StyleManager.addBuiltIn("min-width", {
    ...StyleManager.getBuiltIn("width")!,
    label: "Min width",
    property: "min-width",
  });
  StyleManager.addBuiltIn("max-height", {
    ...StyleManager.getBuiltIn("height")!,
    label: "Max height",
    property: "max-height",
  });
  StyleManager.addBuiltIn("gap", gapProperty);
  StyleManager.addBuiltIn("object-fit", objectFitProperty);
};

const configureBuiltInTypes = (editor: Editor) => {
  const { StyleManager, Components } = editor;

  Components.addType("default", {
    model: {
      defaults: {
        name: "Box",
      },
    },
  });

  Components.addType("text", {
    model: {
      defaults: {
        stylable: [
          "text-align",
          "font-size",
          "font-weight",
          "color",
          "line-height",
          "letter-spacing",
          "text-decoration",
        ],
        traits: [],
        draggable: ".box",
      },
    },
  });

  Components.addType("link", {
    model: {
      defaults: {
        attributes: {
          href: "#",
          target: "_blank",
        },
        traits: [
          {
            type: "text",
            name: "href",
            default: "#",
            label: "Link",
          },
          {
            type: "select",
            name: "target",
            label: "Target",
            options: [
              { id: "_blank", label: "New tab" },
              { id: "_self", label: "Same tab" },
            ],
          },
        ],
        stylable: [
          "text-align",
          "font-size",
          "font-weight",
          "color",
          "line-height",
          "letter-spacing",
          "text-decoration",
        ],
        draggable: ".box",
      },
    },
  });

  StyleManager.addSector("image", {
    name: "Image",
    open: true,
    properties: [StyleManager.getBuiltIn("object-fit")!],
  });

  Components.addType("image", {
    model: {
      defaults: {
        traits: [
          {
            type: "text",
            name: "src",
            default: "link to image",
            label: "Image URL",
          },
        ],
        // stylable: [
        //   "display",
        //   "width",
        //   "height",
        //   "max-width",
        //   "min-width",
        //   "max-height",
        //   "min-height",
        //   "object-fit",
        // ],
        "stylable-require": ["object-fit"],
      },
    },
  });
};

const configureSections = (editor: Editor) => {
  const { StyleManager } = editor;

  StyleManager.addProperty("flex", StyleManager.getBuiltIn("gap")!, {
    at: 1,
  });

  StyleManager.addProperty("dimension", StyleManager.getBuiltIn("min-width")!, {
    at: 2,
  });

  StyleManager.addProperty(
    "dimension",
    StyleManager.getBuiltIn("max-height")!,
    {
      at: 5,
    }
  );

  // Remove properties for simple editing
  StyleManager.removeProperty("general", "float");
  // StyleManager.removeProperty("general", "position");
  // StyleManager.removeProperty("general", "top");
  // StyleManager.removeProperty("general", "right");
  // StyleManager.removeProperty("general", "bottom");
  // StyleManager.removeProperty("general", "left");

  // Display
  // TODO JJ: Images need a different default value and options
  StyleManager.removeProperty("general", "display");
  StyleManager.addProperty(
    "general",
    {
      id: "display",
      type: "select",
      label: "Display",
      property: "display",
      default: "block",
      options: [
        { id: "none", label: "None" },
        { id: "inline", label: "Inline" },
        { id: "block", label: "Block" },
        { id: "flex", label: "Flex" },
        { id: "grid", label: "Grid" },
      ],
    },
    {
      at: 0,
    }
  );

  StyleManager.addProperty(
    "general",
    {
      id: "overflow",
      type: "select",
      label: "Overflow",
      property: "overflow",
      default: "auto",
      options: [
        { id: "auto", label: "Auto" },
        { id: "hidden", label: "Hidden" },
      ],
    },
    {
      at: 0,
    }
  );

  StyleManager.addProperty(
    "dimension",
    {
      id: "aspect-ratio",
      type: "text",
      label: "Aspect Ratio",
      property: "aspect-ratio",
      default: "auto",
    },
    {
      at: 0,
    }
  );

  StyleManager.addSector("grid", {
    name: "Grid",
    open: true,
    properties: [
      {
        type: "select",
        label: "Columns",
        property: "grid-template-columns",
        default: "1fr 1fr 1fr",
        options: [
          { id: "1fr", label: "1 Column" },
          { id: "1fr 1fr", label: "2 Columns" },
          { id: "1fr 1fr 1fr", label: "3 Columns" },
        ],
        requires: {
          display: ["grid"],
        },
      },
      {
        type: "number",
        label: "Column gap",
        property: "column-gap",
        units: ["px"],
        min: 0,
        default: "16",
        requires: {
          display: ["grid"],
        },
      },
      {
        type: "number",
        label: "Row gap",
        property: "row-gap",
        units: ["px"],
        min: 0,
        default: "16",
        requires: {
          display: ["grid"],
        },
      },
    ],
  });
};

const configureTypography = (editor: Editor) => {
  const { StyleManager } = editor;
  // Text shadow
  StyleManager.removeProperty("typography", "text-shadow");

  // Font family
  StyleManager.removeProperty("typography", "font-family");
  StyleManager.addProperty(
    "typography",
    {
      id: "font-family",
      type: "select",
      label: "Font family",
      property: "font-family",
      default: "Aeonik Pro, sans-serif",
      options: [{ id: "Aeonik Pro, sans-serif", name: "Aeonik Pro" }],
    },
    {
      at: 0,
    }
  );
  // Font size
  StyleManager.removeProperty("typography", "font-size");
  StyleManager.addProperty(
    "typography",
    {
      id: "font-size",
      type: "number",
      label: "Font size",
      property: "font-size",
      units: ["px"],
      min: 0,
      default: "16px",
    },
    {
      at: 1,
    }
  );

  // Font weight
  StyleManager.removeProperty("typography", "font-weight");
  StyleManager.addProperty(
    "typography",
    {
      id: "font-weight",
      type: "select",
      label: "Font weight",
      property: "font-weight",
      default: "400",
      options: [
        { id: "400", name: "Regular" },
        { id: "500", name: "Medium" },
      ],
    },
    {
      at: 2,
    }
  );

  StyleManager.addProperty("typography", {
    id: "text-decoration",
    type: "select",
    label: "Text decoration",
    property: "text-decoration",
    default: "none",
    options: [
      { id: "none", name: "None" },
      { id: "underline", name: "Underline" },
      { id: "line-through", name: "Line through" },
    ],
  });
};
